/**
 * faction-playstyles.ts — 5 英雄 slot-0 主玩法 (pure logic, no Phaser).
 *
 * 主人翁系統 v1 (docs/spec/HERO_SYSTEM_V1.md §1.2):選英雄 = 選玩法。每個門派
 * slot-0 主動技手感不同(非換皮):
 *   - celestial 星算:爆發連射 5 發(現況,scene 端 _spawnBurstBullet 排程)
 *   - runic 符文:展開符文盾(applyShield,擋 N 發,吃傷活久)
 *   - techno 機械:偵察提速(startScan 命中加傷視窗 + buffAutoFire 射速)
 *   - elemental 自然:火柱 DoT(spawnFirePillar 前方鋪持續傷害場)
 *   - dominion 疆域:範圍轟炸(damageAllEnemies 一次清場 AOE,CD 較長)
 *
 * 學習與遊戲分離鐵則:此檔 0 答題、0 progress 寫入。只決定純遊戲玩法數值 +
 * 一個 timer/charge 狀態機,scene 端把它 wire 到 Phaser 上。完全 unit-testable。
 *
 * INVARIANT: pure functions + a tiny FactionPlaystyleState class (wall-clock fed
 * by caller). No Phaser / DOM dependency.
 */

import type { FactionId } from '../../shared/types';

/** Per-hero slot-0 HUD presentation + cooldown. Drives _initSkillSystem HUD. */
export interface Slot0Config {
  /** Emoji icon shown in the HUD slot. */
  icon: string;
  /** Short kid-readable label under the icon. */
  label: string;
  /** Slot cooldown in ms. */
  cooldownMs: number;
  /** HUD accent color (slot stroke / burst FX tint). */
  color: number;
}

/** 5 門派 slot-0 設定。celestial 維持 BURST 的 3s CD;其餘各派各自調。 */
export const SLOT0_CONFIG: Record<FactionId, Slot0Config> = {
  celestial: { icon: '⚡', label: '連發',   cooldownMs: 3000, color: 0x9c4dff },
  runic:     { icon: '🔮', label: '符文盾', cooldownMs: 7000, color: 0x4fc3f7 },
  techno:    { icon: '🤖', label: '偵察',   cooldownMs: 6000, color: 0x80cbc4 },
  elemental: { icon: '🌀', label: '火柱',   cooldownMs: 6500, color: 0x66bb6a },
  dominion:  { icon: '🏰', label: '轟炸',   cooldownMs: 9000, color: 0xef5350 },
};

// ── Per-faction effect tuning (balance — 先求能玩、有差異) ──────────────────────

/** runic 符文盾:吸收 N 發攻擊。吃傷活久取向。 */
export const RUNIC_SHIELD = { hitsAbsorbed: 4 } as const;

/** techno 偵察:scan 視窗內所有子彈 +bonusDmg,並提升射速(間隔縮短)。 */
export const TECHNO_SCAN = {
  windowMs: 4000,
  /** Flat bonus damage added to every bullet hit during the scan window. */
  bonusDmg: 6,
  /** Auto-fire interval (ms) during the scan window — faster than the
   *  default ~200-350ms base so the kid feels the speed spike. */
  autoFireIntervalMs: 120,
} as const;

/** elemental 火柱:在玩家前方鋪一個持續傷害場 (DoT)。 */
export const ELEMENTAL_FIRE_PILLAR = {
  durationMs: 4000,
  // boss 2026-06-16「大招傷害太低」(魔王 HP 剛 ×10):火柱 DoT/tick 大幅上調(4s/400ms
  // = ~10 跳,單場 ~400 總傷)。
  dmgPerTick: 40,
  intervalMs: 400,
  /** Field is a vertical band in front of the player. */
  width: 120,
  height: 200,
  /** Offset of the field centre ahead of the player (x+). */
  offsetX: 180,
} as const;

// boss 2026-06-16「大招傷害太低」(魔王 HP 剛 ×10,招式相對變弱):五招 AoE 大幅上調,
// 讓大招對魔王是「有感大塊傷害」。疆域 CD 最長 → 最重;星算/符文次之;techno CD 短稍低。
// 仍會吃 _masteryDamageMult(學習投入越多越痛)。對雜兵是秒殺(大招本就該清場)。
/** dominion 範圍轟炸:一次對全場敵人造成傷害。CD 最長 → 傷害最高。 */
export const DOMINION_BOMBARD = { damageAll: 450 } as const;
/** runic 符文審判:符文爆對全場敵人造成傷害。 */
export const RUNIC_BLAST = { damageAll: 350 } as const;
/** techno 雷達齊射:鎖定全場敵人後導彈齊射造成傷害(CD 短,稍低)。 */
export const TECHNO_BARRAGE = { damageAll: 300 } as const;
/** celestial 星辰隕落:藍金流星砸全場敵人造成傷害(原本只連射,補 AoE 讓大招有感)。 */
export const CELESTIAL_STARFALL = { damageAll: 350 } as const;

/**
 * One in-flight fire-pillar field (elemental). Pure geometry + DoT scheduling;
 * scene renders + applies damage to enemies inside the AABB.
 */
export interface FirePillarField {
  cx: number;
  cy: number;
  width: number;
  height: number;
  dmgPerTick: number;
  intervalMs: number;
  endMs: number;
  /** Next wall-clock ms a damage tick is due. */
  nextTickMs: number;
}

/**
 * Per-run playstyle state machine. Holds the transient buffs/fields a hero's
 * slot-0 (and the matching ActivationOutput methods) produce. The scene feeds
 * wall-clock `nowMs`; all queries are pure reads.
 *
 * Distinct from the celestial burst scheduling which still lives directly in
 * the scene (`_burstShotsLeft` / `_burstNextShotMs`) — that predates this and
 * is left as-is to avoid regressing v11-skill-pilot.
 */
export class FactionPlaystyleState {
  /** runic: remaining shield charges (absorbs the next N player hits). */
  private _shieldHits = 0;
  /** techno: scan window end (wall-clock ms). 0 = inactive. */
  private _scanUntilMs = 0;
  /** techno: per-hit bonus damage active during the scan window. */
  private _scanBonusDmg = 0;
  /** techno: auto-fire interval override end (wall-clock ms). 0 = inactive. */
  private _autoFireUntilMs = 0;
  /** techno: auto-fire interval (ms) while the buff is active. */
  private _autoFireIntervalMs = 0;
  /** elemental: damage multiplier buff end (wall-clock ms). 0 = inactive. */
  private _dmgBuffUntilMs = 0;
  /** elemental: damage multiplier while the buff is active. */
  private _dmgBuffMult = 1;
  /** elemental: active fire-pillar fields. */
  private _firePillars: FirePillarField[] = [];

  /** Reset all transient state (scene (re)start). */
  reset(): void {
    this._shieldHits = 0;
    this._scanUntilMs = 0;
    this._scanBonusDmg = 0;
    this._autoFireUntilMs = 0;
    this._autoFireIntervalMs = 0;
    this._dmgBuffUntilMs = 0;
    this._dmgBuffMult = 1;
    this._firePillars = [];
  }

  // ── runic shield ─────────────────────────────────────────────────────────

  /** Add shield charges (applyShield). */
  applyShield(hitsAbsorbed: number): void {
    this._shieldHits = Math.max(this._shieldHits, Math.max(0, Math.floor(hitsAbsorbed)));
  }

  /** True while at least one shield charge remains. */
  hasShield(): boolean {
    return this._shieldHits > 0;
  }

  shieldHits(): number {
    return this._shieldHits;
  }

  /**
   * Consume one shield charge to absorb an incoming player hit.
   * @returns true if the hit was absorbed (player takes 0 damage), false if no
   *          shield was available (caller applies damage normally).
   */
  absorbHit(): boolean {
    if (this._shieldHits <= 0) return false;
    this._shieldHits--;
    return true;
  }

  // ── techno scan + autofire ───────────────────────────────────────────────

  /** startScan: open a bonus-damage window for durationMs. */
  startScan(nowMs: number, durationMs: number, bonusDmg: number): void {
    this._scanUntilMs = nowMs + durationMs;
    this._scanBonusDmg = Math.max(0, bonusDmg);
  }

  /** True while the scan window is open. */
  isScanning(nowMs: number): boolean {
    return nowMs < this._scanUntilMs;
  }

  /** Per-hit flat bonus damage (0 when not scanning). */
  scanBonusDmg(nowMs: number): number {
    return this.isScanning(nowMs) ? this._scanBonusDmg : 0;
  }

  /** buffAutoFire: shorten the auto-fire interval for durationMs. */
  buffAutoFire(nowMs: number, intervalMs: number, durationMs: number): void {
    this._autoFireUntilMs = nowMs + durationMs;
    this._autoFireIntervalMs = Math.max(1, intervalMs);
  }

  /** True while the auto-fire buff is active. */
  hasAutoFireBuff(nowMs: number): boolean {
    return nowMs < this._autoFireUntilMs;
  }

  /**
   * Effective auto-fire interval: the buffed interval when active, else the
   * passed-in base. Pure — caller supplies its current base interval.
   */
  effectiveFireIntervalMs(nowMs: number, baseIntervalMs: number): number {
    return this.hasAutoFireBuff(nowMs) ? this._autoFireIntervalMs : baseIntervalMs;
  }

  // ── elemental damage buff + fire pillars ─────────────────────────────────

  /** buffPlayerDamage: multiply all player bullet damage for durationMs. */
  buffPlayerDamage(nowMs: number, mult: number, durationMs: number): void {
    this._dmgBuffUntilMs = nowMs + durationMs;
    this._dmgBuffMult = Math.max(1, mult);
  }

  /** Current player damage multiplier (1 when no buff active). */
  damageMult(nowMs: number): number {
    return nowMs < this._dmgBuffUntilMs ? this._dmgBuffMult : 1;
  }

  /** spawnFirePillar: add a DoT field at (cx,cy). */
  spawnFirePillar(
    nowMs: number,
    cx: number,
    cy: number,
    width: number,
    height: number,
    durationMs: number,
    dmgPerTick: number,
    intervalMs: number,
  ): FirePillarField {
    const field: FirePillarField = {
      cx, cy, width, height,
      dmgPerTick: Math.max(0, dmgPerTick),
      intervalMs: Math.max(1, intervalMs),
      endMs: nowMs + durationMs,
      nextTickMs: nowMs + intervalMs,
    };
    this._firePillars.push(field);
    return field;
  }

  /** Active (non-expired) fire-pillar fields. */
  firePillars(nowMs: number): FirePillarField[] {
    return this._firePillars.filter((f) => nowMs < f.endMs);
  }

  /** Drop expired fire-pillar fields. Returns the ones removed (for FX cleanup). */
  pruneFirePillars(nowMs: number): FirePillarField[] {
    const expired = this._firePillars.filter((f) => nowMs >= f.endMs);
    this._firePillars = this._firePillars.filter((f) => nowMs < f.endMs);
    return expired;
  }

  /**
   * Advance a field's tick clock. Returns the number of damage ticks that are
   * due at `nowMs` (usually 0 or 1; >1 only on a long frame stall) and rolls
   * `nextTickMs` forward accordingly. Caller applies `field.dmgPerTick` × ticks
   * to enemies inside the field AABB.
   */
  consumeFirePillarTicks(field: FirePillarField, nowMs: number): number {
    if (nowMs < field.nextTickMs) return 0;
    let ticks = 0;
    while (field.nextTickMs <= nowMs && ticks < 16) {
      field.nextTickMs += field.intervalMs;
      ticks++;
    }
    return ticks;
  }

  /** AABB hit-test: is (x,y) inside the given fire-pillar field? */
  static pointInField(field: FirePillarField, x: number, y: number): boolean {
    return (
      x >= field.cx - field.width / 2 &&
      x <= field.cx + field.width / 2 &&
      y >= field.cy - field.height / 2 &&
      y <= field.cy + field.height / 2
    );
  }
}
